import { useEffect, useState } from "react";
import { FileText } from "lucide-react";

const GuestFolioReport = ({ guestFolio }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedGuest, setSelectedGuest] = useState("0");

  const guests =
    guestFolio?.map((item, idx) => ({
      id: idx.toString(),
      name: item?.guest.first_name || `Guest ${idx + 1}`,
      room: item.room_name || "-",
      folioNumber: item.folios?.[0]?.folio_number || "-",
      original: item,
    })) || [];

  const filteredGuests = guests.filter(
    (guest) =>
      guest.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      guest.room.includes(searchTerm) ||
      guest.folioNumber.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedData = guests.find((g) => g.id === selectedGuest)?.original;
  const selectedFolio = selectedData?.folios?.[0];

  const getTotalCharges = () =>
    selectedFolio?.charges?.reduce(
      (sum, charge) =>
        sum +
        parseFloat(
          parseFloat(charge.amount) +
            parseFloat(
              charge?.charge_type === "room_charge" ||
                charge?.charge_type === "service_charge"
                ? (charge.amount * (charge.tax_rate || 18)) / 100
                : 0
            )
        ),
      0
    ) || 0;

  const getTotalPayments = () =>
    selectedFolio?.charges?.filter(item=>item?.payment_status)?.reduce(
      (sum, charge) =>
        sum +
        parseFloat(
          parseFloat(charge.amount) +
            parseFloat(
              charge?.charge_type === "room_charge" ||
                charge?.charge_type === "service_charge"
                ? (charge.amount * (charge.tax_rate || 18)) / 100
                : 0
            )
        ),
      0
    ) || 0;

  const getBadgeClass = (type) => {
    const map = {
      room_charge: "bg-primary text-white",
      restaurant: "bg-success text-white",
      service_charge: "bg-info text-white",
      tax: "bg-secondary text-white",
      amenity: "bg-warning text-dark",
    };
    return `badge ${map[type] || "bg-light text-dark"}`;
  };

  const chargesMap = new Map([
    ["restaurant", "Restaurant"],
    ["room_charge", "Room Charge"],
    ["service_charge", "Service Charge"],
    ["restaurant", "Restaurant"], // duplicate key, will override previous
  ]);

  return (
    <div className="container mt-4">
      <div className="row g-4">
        {/* Guest Selection */}
        <div className="col-lg-4">
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between">
              <h5 className="mb-0">Select Guest</h5>
            </div>
            <div className="card-body">
              {filteredGuests.map((guest) => (
                <div
                  key={guest?.id}
                  className={`p-2 border rounded mb-2 cursor-pointer ${
                    selectedGuest === guest.id ? "border-primary bg-light" : ""
                  }`}
                  onClick={() => setSelectedGuest(guest?.id)}
                >
                  <strong>{guest.name}</strong>
                  <div className="text-muted small">
                    Room {guest?.room} • {guest?.folioNumber}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Folio Report */}
        <div className="col-lg-8">
          <div className="card">
            <div className="card-header d-flex align-items-center gap-2">
              <FileText size={20} />
              <div>
                <h5 className="mb-0">Guest Folio</h5>
                {selectedData && (
                  <small className="text-muted">
                    Detailed billing for {selectedData?.guest_name}
                  </small>
                )}
              </div>
            </div>
            <div className="card-body">
              {selectedData ? (
                <>
                  {/* Guest & Reservation Info */}
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <h6>Guest Info</h6>
                      <p>
                        <strong>Name:</strong> {selectedData?.guest?.first_name}{" "}
                        {selectedData?.guest?.last_name}
                      </p>
                      <p>
                        <strong>Phone:</strong>{" "}
                        {selectedData?.guest.phone || "N/A"}
                      </p>
                      <p>
                        <strong>Email:</strong>{" "}
                        {selectedData?.guest.email || "N/A"}
                      </p>
                    </div>
                    <div className="col-md-6 mb-3">
                      <h6>Reservation Info</h6>
                      <p>
                        <strong>Booking ID:</strong> {selectedData.booking_id}
                      </p>
                      <p>
                        <strong>Room:</strong> {selectedData.room_name || "N/A"}
                      </p>
                      <p>
                        <strong>Check-in:</strong>{" "}
                        {new Date(
                          selectedData.check_in_date_time
                        ).toLocaleDateString()}
                      </p>
                      <p>
                        <strong>Check-out:</strong>{" "}
                        {new Date(
                          selectedData.check_out_date_time
                        ).toLocaleDateString()}
                      </p>
                    </div>
                  </div>

                  <hr />

                  {/* Summary */}
                  <div className="row text-center mb-4">
                    <div className="col">
                      <h4 className="text-primary">
                        ₹{getTotalCharges().toFixed(2)}
                      </h4>
                      <small>Total Charges</small>
                    </div>
                    <div className="col">
                      <h4 className="text-success">
                        ₹{getTotalPayments().toFixed(2)}
                      </h4>
                      <small>Total Payments</small>
                    </div>
                    <div className="col">
                      <h4 className="text-warning">
                        ₹
                        {parseFloat(
                          Math.abs(getTotalCharges() - getTotalPayments())
                        ).toFixed(2)}
                      </h4>
                      <small>Balance Due</small>
                    </div>
                    <div className="col">
                      <span className="badge bg-secondary">
                        {selectedFolio?.status}
                      </span>
                      <br />
                      <small className="text-muted">Folio Status</small>
                    </div>
                  </div>

                  {/* Charges Table */}
                  <div className="table-responsive">
                    <h6>Charges</h6>
                    <table className="table table-bordered table-sm">
                      <thead className="table-light">
                        <tr>
                          <th>Date</th>
                          <th>Description</th>
                          <th>Type</th>
                          <th>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedFolio?.charges?.map((charge, index) => (
                          <tr key={index}>
                            <td>
                              {new Date(
                                charge.charge_date
                              ).toLocaleDateString()}
                            </td>
                            <td>{charge.description}</td>
                            <td>
                              <span
                                className={getBadgeClass(charge.charge_type)}
                              >
                                {chargesMap.get(charge.charge_type)}
                              </span>
                            </td>
                            <td>₹{parseFloat((charge.charge_type==="room_charge"|| charge.charge_type==="service_charge") ? charge.amount*(parseFloat(charge.tax_rate)+100)/100 : charge.amount).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div className="text-center text-muted py-5">
                  <FileText size={48} className="mb-3" />
                  <p>Select a guest to view their folio details.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuestFolioReport;
